import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsLevelsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({ title: '', subtitle: '', description: '', levels: [] });
  const [pendingFields, setPendingFields] = useState({ title: false, subtitle: false, description: false });
  const [pendingLevels, setPendingLevels] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [editingIdx, setEditingIdx] = useState(null);
  const [tempTag, setTempTag] = useState({});
  const [selectedLevelForModal, setSelectedLevelForModal] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(`${apiBase}/admissions/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const url = m.url || m.file_path;
          if (id && url) map[id] = map[String(id)] = url;
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsLevelsForm] Media error:', err.message);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await loadMediaMap();
      
      const response = await fetch(`${apiBase}/admissions/cms/sections`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(response.status);
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'admissions-levels');
        if (section) {
          setSectionId(section.id);
          let content = section.content_published;
          if (typeof content === 'string') content = JSON.parse(content);
          const en = content?.en || {};
          const newData = {
            title: en.title || '',
            subtitle: en.subtitle || '',
            description: en.description || '',
            levels: Array.isArray(en.levels) ? en.levels : []
          };
          setFormData(newData);
          setOriginalData(newData);

          const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (pRes.ok) {
            const pResult = await pRes.json();
            if (pResult.success && pResult.data?.pending_changes) {
              const pMap = { title: false, subtitle: false, description: false };
              const lMap = {};
              pResult.data.pending_changes.forEach(c => {
                if (c.status === 'pending' && c.locale === 'en') {
                  if (['title', 'subtitle', 'description'].includes(c.field_id)) pMap[c.field_id] = true;
                  else if (c.field_id?.includes('levels.')) {
                    const m = c.field_id.match(/levels\.(\d+)\./);
                    if (m) lMap[`level_${m[1]}`] = true;
                  }
                }
              });
              setPendingFields(pMap);
              setPendingLevels(lMap);
            }
          }
        }
      }
    } catch (err) {
      console.error('[AdmissionsLevelsForm] Load error:', err.message);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => setFormData(prev => ({...prev, [name]: value}));
  
  const handleLevelChange = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === idx ? {...l, [field]: value} : l)
    }));
  };

  const handleAddTag = (idx, type, value) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === idx ? {...l, [type]: [...(l[type] || []), value.trim()]} : l)
    }));
    setTempTag(p => ({...p, [`${idx}-${type}`]: ''}));
  };

  const handleRemoveTag = (idx, type, tagIdx) => {
    setFormData(prev => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === idx ? {...l, [type]: l[type].filter((_, ti) => ti !== tagIdx)} : l)
    }));
  };

  const handleImageUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) { toast.error(`Max 5MB`); return; }
    if (!ALLOWED_TYPES.includes(file.type)) { toast.error('Only JPG/PNG/WebP'); return; }

    setImageUploading(p => ({...p, [idx]: true}));
    try {
      const fData = new FormData();
      fData.append('file', file);
      const xhr = new XMLHttpRequest();

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const res = JSON.parse(xhr.responseText);
            if (res.success || res.ok) {
              const mid = res.media_id || res.id;
              if (mid) {
                handleLevelChange(idx, 'image', mid);
                toast.success('✓ Image uploaded');
              }
            } else toast.error(res.error || 'Upload failed');
          } catch (e) { toast.error('Invalid response'); }
        } else toast.error('Upload failed');
        setImageUploading(p => ({...p, [idx]: false}));
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload error');
        setImageUploading(p => ({...p, [idx]: false}));
      });

      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-levels`);
      xhr.withCredentials = true;
      xhr.send(fData);
    } catch (err) {
      toast.error('Error: ' + err.message);
      setImageUploading(p => ({...p, [idx]: false}));
    }
  };

  const handleSubmitField = async (name) => {
    if (!sectionId) { toast.error('No section'); return; }
    if (!formData[name]) { toast.warning(`Fill ${name}`); return; }
    if (formData[name] === originalData[name]) { toast.warning('No changes'); return; }

    setSubmitting(p => ({...p, [name]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: name,
          old_value: originalData[name],
          new_value: formData[name],
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Submitted');
        setPendingFields(p => ({...p, [name]: true}));
        setOriginalData(p => ({...p, [name]: formData[name]}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [name]: false}));
    }
  };

  const handleSubmitLevel = async (idx) => {
    if (!sectionId) { toast.error('No section'); return; }
    const level = formData.levels[idx];
    if (!level?.title) { toast.warning('Fill title'); return; }

    const key = `level_${idx}`;
    setSubmitting(p => ({...p, [key]: true}));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `levels.${idx}.update`,
          field_name: `Level: ${level.title}`,
          old_value: originalData.levels[idx] || {},
          new_value: level,
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || res.ok) {
        toast.success('Level submitted');
        setPendingLevels(p => ({...p, [key]: true}));
        setOriginalData(p => ({...p, levels: p.levels.map((l, i) => i === idx ? level : l)}));
      } else toast.error(result.error || 'Failed');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({...p, [key]: false}));
    }
  };

  const handleDeleteLevel = (idx) => {
    if (window.confirm('Delete level?')) {
      setFormData(prev => ({...prev, levels: prev.levels.filter((_, i) => i !== idx)}));
      setPendingLevels(p => { const u = {...p}; delete u[`level_${idx}`]; return u; });
    }
  };

  const handleAddLevel = () => {
    setFormData(prev => ({
      ...prev,
      levels: [...prev.levels, { title: '', note: '', ageRange: '', description: '', image: '', intake: '', assessment: '', support: '', highlights: [], process: '', outcomes: [], timeline: '' }]
    }));
  };

  if (loading) return <div className="hero-form-wrapper"><div className="loading">Loading...</div></div>;

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />
      <div className="hero-form-header">
        <h1 className="hero-form-title">Admissions Levels</h1>
        <p className="hero-form-description">Edit title, subtitle, description & add level pathways</p>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.title} onChange={e => handleFieldChange('title', e.target.value)} disabled={submitting.title || pendingFields.title} placeholder="Section title..." className="text-input" />
        <button onClick={() => handleSubmitField('title')} disabled={submitting.title || pendingFields.title || formData.title === originalData.title} className="submit-btn">
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Subtitle */}
      <div className="form-section">
        <div className="field-header">
          <label>Subtitle</label>
          {pendingFields.subtitle && <span className="status-badge pending">Pending</span>}
        </div>
        <input type="text" value={formData.subtitle} onChange={e => handleFieldChange('subtitle', e.target.value)} disabled={submitting.subtitle || pendingFields.subtitle} placeholder="Subtitle..." className="text-input" />
        <button onClick={() => handleSubmitField('subtitle')} disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle} className="submit-btn">
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Description */}
      <div className="form-section">
        <div className="field-header">
          <label>Description</label>
          {pendingFields.description && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea value={formData.description} onChange={e => handleFieldChange('description', e.target.value)} disabled={submitting.description || pendingFields.description} placeholder="Description..." className="textarea-input" rows="3" />
        <button onClick={() => handleSubmitField('description')} disabled={submitting.description || pendingFields.description || formData.description === originalData.description} className="submit-btn">
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Levels */}
      <div className="form-section">
        <div className="items-section-header">
          <h2 className="form-section-title">Level Pathways</h2>
          <button type="button" onClick={handleAddLevel} className="btn-add-item">+ Add Level</button>
        </div>

        {formData.levels.length === 0 ? (
          <div className="items-empty-state"><p>No levels. Click "Add Level"</p></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {formData.levels.map((level, idx) => {
              const key = `level_${idx}`;
              const isPending = pendingLevels[key];
              return (
                <div key={idx} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {/* Compact Card Image */}
                  <div style={{ height: '180px', background: '#f3f4f6', overflow: 'hidden' }}>
                    {level.image && mediaMap[level.image] ? (
                      <img src={mediaMap[level.image]} alt={`Level ${idx + 1}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)', color: '#9ca3af', fontSize: '0.875rem' }}>
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Compact Card Content */}
                  <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#1f2937', lineHeight: 1.3 }}>{level.title || 'Untitled'}</h3>
                    {level.note && <span style={{ fontSize: '0.75rem', background: '#fef3c7', color: '#92400e', padding: '0.25rem 0.5rem', borderRadius: '4px', fontWeight: '600', width: 'fit-content' }}>{level.note}</span>}
                    {level.ageRange && <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>{level.ageRange}</p>}
                    {level.description && <p style={{ margin: 0, fontSize: '0.875rem', color: '#4b5563', lineHeight: 1.5 }}>{level.description.substring(0, 80)}...</p>}
                  </div>

                  {/* Card Actions */}
                  <div style={{ padding: '0.75rem', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={() => setSelectedLevelForModal(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Edit
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleDeleteLevel(idx)}
                      disabled={isPending}
                      style={{ flex: 1, padding: '0.5rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}
                    >
                      Delete
                    </button>
                  </div>

                  {isPending && <div style={{ padding: '0.5rem', background: '#fef08a', color: '#92400e', textAlign: 'center', fontSize: '0.75rem', fontWeight: '600' }}>Pending Approval</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Level Modal */}
      {selectedLevelForModal !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setSelectedLevelForModal(null)}>
          <div style={{ background: 'white', borderRadius: '12px', maxHeight: '90vh', overflow: 'auto', maxWidth: '700px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Edit Level</h2>
              <button type="button" onClick={() => setSelectedLevelForModal(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Title *</label>
                <input 
                  type="text" 
                  value={formData.levels[selectedLevelForModal].title || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'title', e.target.value)} 
                  placeholder="e.g., Preschool..." 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Note */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Note</label>
                <input 
                  type="text" 
                  value={formData.levels[selectedLevelForModal].note || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'note', e.target.value)} 
                  placeholder="e.g., Trial class available" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Age Range */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Age Range</label>
                <input 
                  type="text" 
                  value={formData.levels[selectedLevelForModal].ageRange || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'ageRange', e.target.value)} 
                  placeholder="e.g., Ages 3-6" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Description</label>
                <textarea 
                  value={formData.levels[selectedLevelForModal].description || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'description', e.target.value)} 
                  placeholder="Description..." 
                  rows="3"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Image */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Image</label>
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/webp" 
                  onChange={e => handleImageUpload(selectedLevelForModal, e)} 
                  disabled={imageUploading[selectedLevelForModal]} 
                  id={`level-modal-${selectedLevelForModal}`}
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
                {imageUploading[selectedLevelForModal] && <p style={{ margin: '0.5rem 0 0 0', color: '#3b82f6', fontSize: '0.875rem' }}>Uploading...</p>}
              </div>

              {/* Intake */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Intake</label>
                <textarea 
                  value={formData.levels[selectedLevelForModal].intake || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'intake', e.target.value)} 
                  placeholder="Intake details..." 
                  rows="2"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Assessment */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Assessment</label>
                <textarea 
                  value={formData.levels[selectedLevelForModal].assessment || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'assessment', e.target.value)} 
                  placeholder="Assessment..." 
                  rows="2"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Support */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Support</label>
                <textarea 
                  value={formData.levels[selectedLevelForModal].support || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'support', e.target.value)} 
                  placeholder="Support provided..." 
                  rows="2"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Process */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Process</label>
                <input 
                  type="text" 
                  value={formData.levels[selectedLevelForModal].process || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'process', e.target.value)} 
                  placeholder="e.g., Inquiry → Assessment → ..." 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Timeline */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Timeline</label>
                <input 
                  type="text" 
                  value={formData.levels[selectedLevelForModal].timeline || ''} 
                  onChange={e => handleLevelChange(selectedLevelForModal, 'timeline', e.target.value)} 
                  placeholder="e.g., 2-3 weeks" 
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                />
              </div>

              {/* Highlights */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Highlights</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input 
                    type="text" 
                    value={tempTag[`${selectedLevelForModal}-highlights`] || ''} 
                    onChange={e => setTempTag(p => ({...p, [`${selectedLevelForModal}-highlights`]: e.target.value}))} 
                    placeholder="Add highlight..." 
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => handleAddTag(selectedLevelForModal, 'highlights', tempTag[`${selectedLevelForModal}-highlights`] || '')} 
                    style={{ padding: '0.75rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Add
                  </button>
                </div>
                {Array.isArray(formData.levels[selectedLevelForModal]?.highlights) && formData.levels[selectedLevelForModal].highlights.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.levels[selectedLevelForModal].highlights.map((tag, ti) => (
                      <span key={ti} style={{ background: '#dbeafe', color: '#1e40af', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(selectedLevelForModal, 'highlights', ti)} style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Outcomes */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1f2937' }}>Outcomes</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input 
                    type="text" 
                    value={tempTag[`${selectedLevelForModal}-outcomes`] || ''} 
                    onChange={e => setTempTag(p => ({...p, [`${selectedLevelForModal}-outcomes`]: e.target.value}))} 
                    placeholder="Add outcome..." 
                    style={{ flex: 1, padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem' }}
                  />
                  <button 
                    type="button" 
                    onClick={() => handleAddTag(selectedLevelForModal, 'outcomes', tempTag[`${selectedLevelForModal}-outcomes`] || '')} 
                    style={{ padding: '0.75rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                  >
                    Add
                  </button>
                </div>
                {Array.isArray(formData.levels[selectedLevelForModal]?.outcomes) && formData.levels[selectedLevelForModal].outcomes.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {formData.levels[selectedLevelForModal].outcomes.map((tag, ti) => (
                      <span key={ti} style={{ background: '#dcfce7', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(selectedLevelForModal, 'outcomes', ti)} style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => handleSubmitLevel(selectedLevelForModal)}
                  disabled={submitting[`level_${selectedLevelForModal}`]}
                  style={{ flex: 1, padding: '0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {submitting[`level_${selectedLevelForModal}`] ? 'Submitting...' : 'Submit'}
                </button>
                <button 
                  type="button"
                  onClick={() => setSelectedLevelForModal(null)}
                  style={{ flex: 1, padding: '0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
